import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';

const StatCard = ({ icon, label, value, color, link }) => (
  <Link to={link} className="stat-card" style={{ '--stat-color': color }}>
    <div className="stat-card__icon">{icon}</div>
    <div className="stat-card__info">
      <div className="stat-card__value">{value ?? '—'}</div>
      <div className="stat-card__label">{label}</div>
    </div>
    <div className="stat-card__arrow">→</div>
  </Link>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [recentProfiles, setRecentProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { error } = useToast();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, postsRes, profilesRes] = await Promise.all([
          api.get('/admin/stats'),
          api.get('/posts'),
          api.get('/matrimony/profiles'),
        ]);
        setStats(statsRes.data);
        setRecentPosts(Array.isArray(postsRes.data) ? postsRes.data.slice(0, 5) : []);
        setRecentProfiles(Array.isArray(profilesRes.data) ? profilesRes.data.slice(0, 5) : []);
      } catch (err) {
        error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [error]);

  const formatDate = (val) => {
    if (!val) return '—';
    if (val?.seconds) return new Date(val.seconds * 1000).toLocaleDateString('en-IN');
    if (typeof val === 'string') return new Date(val).toLocaleDateString('en-IN');
    return '—';
  };

  return (
    <AdminLayout title="Dashboard">
      {/* Stats Row */}
      <div className="stats-row">
        <StatCard icon="📰" label="Total Posts"        value={loading ? '...' : stats?.totalPosts}    color="var(--primary-500)" link="/admin/posts" />
        <StatCard icon="💑" label="Matrimony Profiles" value={loading ? '...' : stats?.totalProfiles} color="var(--accent-500)"  link="/admin/profiles" />
        <StatCard icon="👥" label="Registered Users"   value={loading ? '...' : stats?.totalUsers}    color="var(--gold-700)"    link="/admin/users" />
      </div>

      {/* Quick Actions */}
      <div className="admin-card">
        <h3 className="admin-card__title">⚡ Quick Actions</h3>
        <div className="quick-actions">
          <Link to="/admin/posts"      className="quick-action"><span className="qa-icon">✍️</span><span>Write New Post</span></Link>
          <Link to="/admin/profiles"   className="quick-action"><span className="qa-icon">👁️</span><span>Review Profiles</span></Link>
          <Link to="/admin/users"      className="quick-action"><span className="qa-icon">👑</span><span>Manage Admins</span></Link>
          <Link to="/" target="_blank" className="quick-action"><span className="qa-icon">🌐</span><span>View Website</span></Link>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="admin-grid-2">
        <div className="admin-card">
          <div className="admin-card__header">
            <h3 className="admin-card__title">📰 Recent Posts</h3>
            <Link to="/admin/posts" className="admin-card__link">View All</Link>
          </div>
          {loading ? (
            <div className="admin-loading">Loading...</div>
          ) : recentPosts.length > 0 ? (
            <div className="recent-list">
              {recentPosts.map((post) => (
                <div key={post.id} className="recent-item">
                  <div className="recent-item__title">{post.title}</div>
                  <div className="recent-item__meta">
                    <span className="tag tag--news">{post.category || 'News'}</span>
                    <span>{formatDate(post.createdAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="admin-empty">No posts yet.</p>
          )}
        </div>

        <div className="admin-card">
          <div className="admin-card__header">
            <h3 className="admin-card__title">💑 Recent Profiles</h3>
            <Link to="/admin/profiles" className="admin-card__link">View All</Link>
          </div>
          {loading ? (
            <div className="admin-loading">Loading...</div>
          ) : recentProfiles.length > 0 ? (
            <div className="recent-list">
              {recentProfiles.map((profile) => (
                <div key={profile.id} className="recent-item">
                  <div className="recent-item__title">{profile.fullName}</div>
                  <div className="recent-item__meta">
                    <span className="tag tag--profile">{profile.gender}</span>
                    <span>{profile.age} yrs • {profile.currentResidence || '—'}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="admin-empty">No profiles yet.</p>
          )}
        </div>
      </div>

      <style jsx>{`
        .stats-row {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: var(--space-6);
          margin-bottom: var(--space-6);
        }
        .stat-card {
          background: white;
          border-radius: var(--radius-xl);
          padding: var(--space-6);
          display: flex;
          align-items: center;
          gap: var(--space-4);
          text-decoration: none;
          border: 1px solid var(--neutral-200);
          box-shadow: var(--shadow-sm);
          transition: all var(--transition-base);
          border-left: 4px solid var(--stat-color);
        }
        .stat-card:hover { transform: translateY(-3px); box-shadow: var(--shadow-md); }
        .stat-card__icon { font-size: 2rem; }
        .stat-card__info { flex: 1; }
        .stat-card__value {
          font-family: var(--font-display);
          font-size: var(--text-4xl);
          font-weight: var(--weight-bold);
          color: var(--stat-color);
          line-height: 1;
        }
        .stat-card__label { font-size: var(--text-sm); color: var(--neutral-600); margin-top: var(--space-1); }
        .stat-card__arrow { color: var(--neutral-400); font-size: var(--text-lg); }

        .admin-card {
          background: white;
          border-radius: var(--radius-xl);
          padding: var(--space-6);
          border: 1px solid var(--neutral-200);
          box-shadow: var(--shadow-sm);
          margin-bottom: var(--space-6);
        }
        .admin-card__header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--space-5);
        }
        .admin-card__title { font-size: var(--text-xl); color: var(--primary-900); margin: 0; }
        .admin-card__link { font-size: var(--text-sm); color: var(--primary-500); text-decoration: none; font-weight: var(--weight-medium); }

        .quick-actions {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: var(--space-4);
        }
        .quick-action {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: var(--space-2);
          padding: var(--space-5);
          background: var(--neutral-50);
          border: 2px solid var(--neutral-200);
          border-radius: var(--radius-xl);
          text-decoration: none;
          color: var(--neutral-700);
          font-size: var(--text-sm);
          font-weight: var(--weight-medium);
          transition: all var(--transition-fast);
          text-align: center;
        }
        .quick-action:hover {
          background: var(--primary-50);
          border-color: var(--primary-300);
          color: var(--primary-700);
          transform: translateY(-2px);
        }
        .qa-icon { font-size: 1.8rem; }

        .admin-grid-2 {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: var(--space-6);
        }
        .recent-list { display: flex; flex-direction: column; gap: var(--space-3); }
        .recent-item {
          padding: var(--space-3);
          background: var(--neutral-50);
          border-radius: var(--radius-lg);
          border: 1px solid var(--neutral-200);
        }
        .recent-item__title {
          font-size: var(--text-sm);
          font-weight: var(--weight-semibold);
          color: var(--neutral-900);
          margin-bottom: var(--space-1);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .recent-item__meta {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          font-size: var(--text-xs);
          color: var(--neutral-500);
        }
        .tag {
          padding: 2px var(--space-2);
          border-radius: var(--radius-full);
          font-size: 10px;
          font-weight: var(--weight-bold);
          text-transform: uppercase;
        }
        .tag--news    { background: var(--primary-100); color: var(--primary-700); }
        .tag--profile { background: var(--accent-100);  color: var(--accent-700);  }
        .admin-loading { color: var(--neutral-500); font-size: var(--text-sm); padding: var(--space-4); text-align: center; }
        .admin-empty { color: var(--neutral-500); font-size: var(--text-sm); text-align: center; padding: var(--space-4) 0; margin: 0; }

        @media (max-width: 1024px) {
          .stats-row     { grid-template-columns: 1fr; }
          .quick-actions { grid-template-columns: repeat(2, 1fr); }
          .admin-grid-2  { grid-template-columns: 1fr; }
        }
      `}</style>
    </AdminLayout>
  );
};

export default AdminDashboard;